import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { formatUtilityLabel, getExtractedTotalAmount } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import useStyles from './billDetailsStyles';
import { DASH, formatDate, formatMoney, getCommonServiceDates, getServiceGlCode, getServicePeriods, pickValue, SESSION_ONLY_HINT } from './billDetailsUtils';
import { normalizeAmounts } from './billEditUtils';
import { BillSummaryEditTable } from './BillSummaryEditTable';
import { getServiceIndexes, getServiceTypes, matchBillUtilities, selectServices } from './billUtilityMatching';
import { ConfirmActionModal } from './ConfirmActionModal';
import { ExtraUtilitiesCard } from './ExtraUtilitiesCard';
import { PropertyLineItems } from './PropertyLineItems';
const PUSH_CONFIRM_MESSAGE = 'This will push the verified bill and its mapped charges to Appfolio for the selected properties. ' + 'Make sure the bill details and utility mappings are correct before continuing.';

/** The GL code each utility is billed under, taken from whichever property states it first. */
function getGlCodeByServiceType(data, serviceTypes) {
  const services = ((data === null || data === void 0 ? void 0 : data.properties) || []).flatMap(property => property.services || []);
  return serviceTypes.reduce((codes, serviceType) => {
    const code = services.filter(service => service.serviceType === serviceType).map(getServiceGlCode).find(Boolean);
    return _objectSpread(_objectSpread({}, codes), {}, {
      [serviceType]: code || ''
    });
  }, {});
}

/**
 * Extracted bill summary and per-property line items (Figma: "Bill details" card), covering only the
 * utilities the matched property is set up for. Editing works on a draft that recalculates as it is
 * typed in; saving hands it to `onSave`. Whatever else the bill charges for follows in its own card.
 */
export function BillDetailsCard(_ref) {
  var _view$finalValidation, _view$finalValidation2;
  let billPropertyIds = _ref.billPropertyIds,
    customerName = _ref.customerName,
    data = _ref.data,
    _ref$isAppfolio = _ref.isAppfolio,
    isAppfolio = _ref$isAppfolio === void 0 ? false : _ref$isAppfolio,
    _ref$isPushed = _ref.isPushed,
    isPushed = _ref$isPushed === void 0 ? false : _ref$isPushed,
    onPush = _ref.onPush,
    onSave = _ref.onSave,
    properties = _ref.properties;
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draft = _useState2[0],
    setDraft = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isPushConfirmOpen = _useState4[0],
    setIsPushConfirmOpen = _useState4[1];
  const isEditing = draft !== null;
  const view = isEditing ? draft : data;
  const _useMemo = useMemo(() => {
      const groups = matchBillUtilities({
        billPropertyIds,
        data: view,
        properties
      });
      return {
        covered: selectServices(groups, true),
        extra: selectServices(groups, false)
      };
    }, [billPropertyIds, properties, view]),
    covered = _useMemo.covered,
    extra = _useMemo.extra;
  const summary = useMemo(() => {
    var _pickValue, _pickValue2;
    const serviceTypes = getServiceTypes(covered);
    const coveredServices = covered.flatMap(group => group.services.map(item => item.service));
    return {
      accountNumber: (_pickValue = pickValue(view, ['accountNumber'])) !== null && _pickValue !== void 0 ? _pickValue : DASH,
      addresses: ((view === null || view === void 0 ? void 0 : view.properties) || []).map(property => property.address).filter(Boolean),
      billDate: formatDate(pickValue(view, ['billDate', 'postingDate'])),
      customFields: ((view === null || view === void 0 ? void 0 : view.customFields) || []).filter(field => field.label || field.value),
      dueDate: formatDate(pickValue(view, ['dueDate', 'billDueDate'])),
      glCodeByServiceType: getGlCodeByServiceType(view, serviceTypes),
      // GL codes are only ever pushed to Appfolio, so nobody else is asked for them.
      glCodeServiceTypes: isAppfolio ? serviceTypes : [],
      invoiceNumber: (_pickValue2 = pickValue(view, ['invoiceNumber', 'invoiceNo'])) !== null && _pickValue2 !== void 0 ? _pickValue2 : DASH,
      provider: (view === null || view === void 0 ? void 0 : view.vendorName) || DASH,
      serviceDates: getCommonServiceDates(coveredServices, view),
      servicePeriods: getServicePeriods(coveredServices, view),
      serviceTypes,
      totalDue: formatMoney(getExtractedTotalAmount(view), view === null || view === void 0 ? void 0 : view.currency),
      utilities: serviceTypes.length ? serviceTypes.map(formatUtilityLabel).join(', ') : DASH
    };
  }, [covered, isAppfolio, view]);
  const isVerified = (view === null || view === void 0 || (_view$finalValidation = view.finalValidation) === null || _view$finalValidation === void 0 ? void 0 : _view$finalValidation.isValid) === true;
  const validationReason = (view === null || view === void 0 || (_view$finalValidation2 = view.finalValidation) === null || _view$finalValidation2 === void 0 ? void 0 : _view$finalValidation2.reason) || (isVerified ? 'Provider, utilities, and address match with existing property records.' : 'Some validation checks did not pass. Review the extracted values before continuing.');
  const handleSave = () => {
    onSave(normalizeAmounts(draft));
    setDraft(null);
  };
  const handleConfirmPush = () => {
    setIsPushConfirmOpen(false);
    onPush();
  };
  const emptyStateMessage = ((view === null || view === void 0 ? void 0 : view.properties) || []).length ? 'None of the utilities on this bill are set up on the properties it matched.' : 'No property line items were extracted for this bill.';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(isEditing ? classes.cardOverflowing : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, isEditing ? 'Edit bill details' : 'Bill details'), !isEditing && isAppfolio ? /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(isPushed ? classes.statusChipDone : '')
  }, isPushed ? 'Pushed to Appfolio' : 'Pending Appfolio push') : null), isEditing ? null : /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit bill details",
    className: classes.iconButton,
    disabled: !onSave,
    onClick: () => setDraft(data),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))), isEditing ? /*#__PURE__*/React.createElement(BillSummaryEditTable, {
    customerName: customerName,
    glCodeByServiceType: summary.glCodeByServiceType,
    glCodeServiceTypes: summary.glCodeServiceTypes,
    onChange: setDraft,
    serviceDates: summary.serviceDates,
    serviceTypes: summary.serviceTypes,
    value: draft
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(isVerified ? '' : classes.matchBoxWarning)
  }, isVerified ? /*#__PURE__*/React.createElement(Icon, {
    type: "check-circle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchTitle, " ").concat(isVerified ? '' : classes.matchTitleWarning)
  }, isVerified ? 'Match: Verified' : 'Match: Review recommended'), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchText, " ").concat(isVerified ? '' : classes.matchTextWarning)
  }, validationReason))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Summary:"), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Customer:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, (view === null || view === void 0 ? void 0 : view.customerName) || customerName || DASH)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Provider:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.provider)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Utilities:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.utilities)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Properties:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.addresses.length ? summary.addresses.map(address => /*#__PURE__*/React.createElement("div", {
    key: address
  }, address)) : DASH))), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Account #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.accountNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Invoice #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.invoiceNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Bill date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.billDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Due date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.dueDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Service dates:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.servicePeriods.length ? summary.servicePeriods.map(entry => /*#__PURE__*/React.createElement("div", {
    key: entry.period
  }, summary.servicePeriods.length > 1 ? "".concat(entry.utilities, ": ").concat(entry.period) : entry.period)) : DASH))), summary.glCodeServiceTypes.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.glCodeServiceTypes.map(serviceType => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: serviceType
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, formatUtilityLabel(serviceType), " GL code:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.glCodeByServiceType[serviceType] || DASH)))) : null, summary.customFields.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.customFields.map((field, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: "".concat(field.label, "-").concat(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, field.label || DASH, ":"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, field.value || DASH)))) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Total due:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.totalDue))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Line items by property:")), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editSectionRow
  }, "Line items per property:") : null, covered.length ? covered.map(group => /*#__PURE__*/React.createElement(PropertyLineItems, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view,
    glCodeByServiceType: summary.glCodeByServiceType,
    isEditing: isEditing,
    key: group.propertyIndex,
    onChange: setDraft,
    propertyIndex: group.propertyIndex,
    serviceIndexes: getServiceIndexes(group),
    showGlCode: isAppfolio
  })) : /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, emptyStateMessage), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraft(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : null, !isEditing && isAppfolio ? /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: SESSION_ONLY_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onPush || isPushed,
    onClick: () => setIsPushConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, isPushed ? 'Pushed to Appfolio' : 'Push to Appfolio'))), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Push to Appfolio",
    isOpen: isPushConfirmOpen,
    message: PUSH_CONFIRM_MESSAGE,
    onClose: () => setIsPushConfirmOpen(false),
    onConfirm: handleConfirmPush,
    title: "Push bill to Appfolio?"
  })) : null), extra.length ? /*#__PURE__*/React.createElement(ExtraUtilitiesCard, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view,
    groups: extra
  }) : null);
}